// Trading Page - Path of Exile style trading interface - 2025-01-XX
// Allows players to browse items from online players and whisper them for trades

import { useState, useMemo, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Filter, MessageSquare, RefreshCw, AlertCircle, Loader2, X, List, Grid3x3, Square, ArrowUpDown, ArrowUp, Shield, UtensilsCrossed, Shirt, Wrench, Compass, Crosshair, Sword, Heart, GlassWater, Package } from 'lucide-react';
import { useOnlinePlayerItems, TradingItem } from '../hooks/useOnlinePlayerItems';
import { Logo } from '../components/Logo';

type ViewMode = 'list' | 'grid' | 'detailed';
type SortOption = 'name' | 'level' | 'condition' | 'player' | 'class' | 'uses';

// Icon mapping for item types - 2025-01-XX
const getItemIcon = (className: string) => {
  const iconMap: { [key: string]: any } = {
    'ArmorObject': Shield,
    'Food': UtensilsCrossed,
    'ClothingObject': Shirt,
    'CraftingTool': Wrench,
    'SurveyTool': Compass,
    'RangedWeapon': Crosshair,
    'MeleeWeapon': Sword,
    'WeaponObject': Sword,
    'Medical': Heart,
    'Drink': GlassWater,
  };
  
  // Try exact match first
  if (iconMap[className]) {
    return iconMap[className];
  }
  
  // Try partial matches
  if (className.toLowerCase().includes('armor')) return Shield;
  if (className.toLowerCase().includes('food')) return UtensilsCrossed;
  if (className.toLowerCase().includes('clothing') || className.toLowerCase().includes('clothes')) return Shirt;
  if (className.toLowerCase().includes('craft')) return Wrench;
  if (className.toLowerCase().includes('survey')) return Compass;
  if (className.toLowerCase().includes('ranged') || className.toLowerCase().includes('rifle') || className.toLowerCase().includes('pistol')) return Crosshair;
  if (className.toLowerCase().includes('melee') || className.toLowerCase().includes('sword') || className.toLowerCase().includes('knife')) return Sword;
  if (className.toLowerCase().includes('medical') || className.toLowerCase().includes('med')) return Heart;
  if (className.toLowerCase().includes('drink')) return GlassWater;
  
  // Default icon
  return Package;
};

export function TradingPage() {
  const { items, loading, error, refresh, whisperItem } = useOnlinePlayerItems();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClass, setSelectedClass] = useState<string>('all');
  const [selectedPlayer, setSelectedPlayer] = useState<string>('all');
  const [whisperStatus, setWhisperStatus] = useState<{ itemId: string; success: boolean; error?: string } | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [sortBy, setSortBy] = useState<SortOption>('name');
  const [sortAscending, setSortAscending] = useState<boolean>(true);
  const [visibleItemsCount, setVisibleItemsCount] = useState<number>(30); // Initial items to show - 2025-01-XX
  const observerTarget = useRef<HTMLDivElement>(null); // Ref for infinite scroll trigger - 2025-01-XX
  const [showScrollToTop, setShowScrollToTop] = useState<boolean>(false); // Show scroll to top button - 2025-01-XX
  const [buttonPosition, setButtonPosition] = useState({ bottom: 16, right: 16 }); // Button position for scroll-to-top - 2025-01-XX

  // Disable global background slideshow on trading page - 2025-01-XX
  useEffect(() => {
    document.body.classList.add('trading-page-active');
    document.documentElement.classList.add('trading-page-active');
    return () => {
      document.body.classList.remove('trading-page-active');
      document.documentElement.classList.remove('trading-page-active');
    };
  }, []);

  // Get unique item classes and players for filters - 2025-01-XX
  const itemClasses = useMemo(() => {
    const classes = new Set(items.map(item => item._className));
    return Array.from(classes).sort();
  }, [items]);

  const players = useMemo(() => {
    const playerSet = new Set(items.map(item => item.playerName));
    return Array.from(playerSet).sort();
  }, [items]);

  // Filter and sort items - 2025-01-XX
  const filteredAndSortedItems = useMemo(() => {
    // Filter items
    let filtered = items.filter(item => {
      const matchesSearch = searchQuery === '' || 
        item.displayName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.playerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item._className.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesClass = selectedClass === 'all' || item._className === selectedClass;
      const matchesPlayer = selectedPlayer === 'all' || item.playerName === selectedPlayer;

      return matchesSearch && matchesClass && matchesPlayer;
    });

    // Sort items
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          comparison = (a.displayName || '').localeCompare(b.displayName || '');
          break;
        case 'level':
          comparison = (a.stats?.level || 0) - (b.stats?.level || 0);
          break;
        case 'condition':
          const aCondition = a.stats?.condition && a.stats?.maxCondition 
            ? a.stats.condition / a.stats.maxCondition 
            : 0;
          const bCondition = b.stats?.condition && b.stats?.maxCondition 
            ? b.stats.condition / b.stats.maxCondition 
            : 0;
          comparison = aCondition - bCondition;
          break;
        case 'player':
          comparison = a.playerName.localeCompare(b.playerName);
          break;
        case 'class':
          comparison = a._className.localeCompare(b._className);
          break;
        case 'uses':
          comparison = (a.stats?.useCount || 0) - (b.stats?.useCount || 0);
          break;
      }
      
      return sortAscending ? comparison : -comparison;
    });

    return filtered;
  }, [items, searchQuery, selectedClass, selectedPlayer, sortBy, sortAscending]);

  // Get visible items for display - 2025-01-XX
  const visibleItems = useMemo(() => {
    return filteredAndSortedItems.slice(0, visibleItemsCount);
  }, [filteredAndSortedItems, visibleItemsCount]);

  // Reset visible items count when filters change - 2025-01-XX
  useEffect(() => {
    setVisibleItemsCount(30);
  }, [searchQuery, selectedClass, selectedPlayer, sortBy, sortAscending, viewMode]);

  // Infinite scroll implementation - 2025-01-XX
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleItemsCount((prev) => {
            const totalItems = filteredAndSortedItems.length;
            if (prev < totalItems) {
              // Load more items when the observer target is visible
              return Math.min(prev + 30, totalItems);
            }
            return prev;
          });
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [filteredAndSortedItems.length]);

  // Send whisper to item owner - 2025-01-XX
  const handleWhisper = async (item: TradingItem) => {
    console.log('Whisper button clicked for item:', item);
    console.log('Item details:', {
      _oid: item._oid,
      displayName: item.displayName,
      playerName: item.playerName,
      className: item._className
    });
    
    try {
      console.log('Calling whisperItem function...');
      const result = await whisperItem(item);
      console.log('Whisper result:', result);
      
      if (result.success) {
        console.log('Whisper sent successfully!');
        setWhisperStatus({ itemId: item._oid, success: true });
        setTimeout(() => setWhisperStatus(null), 3000);
      } else {
        console.error('Whisper failed:', result.error);
        setWhisperStatus({ itemId: item._oid, success: false, error: result.error });
        setTimeout(() => setWhisperStatus(null), 5000);
      }
    } catch (err: any) {
      console.error('Error in handleWhisper:', err);
      setWhisperStatus({ itemId: item._oid, success: false, error: err.message || 'Failed to send whisper' });
      setTimeout(() => setWhisperStatus(null), 5000);
    }
  };

  // Scroll to top functionality - 2025-01-XX
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Calculate button position to match Header buttons - 2025-01-XX
  useEffect(() => {
    const calculateButtonPosition = () => {
      const viewportHeight = window.innerHeight;
      const footer = document.querySelector('footer');
      
      let safeBottom = 16; // Default bottom margin
      
      if (footer) {
        const footerRect = footer.getBoundingClientRect();
        const footerTop = footerRect.top;
        
        // If footer is visible and close to bottom, adjust button position
        if (footerTop < viewportHeight && footerTop > viewportHeight - 200) {
          safeBottom = Math.max(16, viewportHeight - footerTop + 16);
        }
      }
      
      setButtonPosition({ bottom: safeBottom, right: 16 });
    };

    calculateButtonPosition();
    const handleResize = () => calculateButtonPosition();
    const handleScroll = () => {
      calculateButtonPosition();
      const scrollThreshold = 400; // Show button after scrolling 400px
      setShowScrollToTop(window.scrollY > scrollThreshold);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className="min-h-screen pt-20 pb-10 relative">
      {/* Static Background - Updated to use freighter.png - 2025-01-XX */}
      <div 
        className="fixed inset-0"
        style={{
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(/media/backgrounds/freighter.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          backgroundRepeat: 'no-repeat',
          zIndex: 0,
          pointerEvents: 'none'
        }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" style={{ position: 'relative' }}>
        {/* Header Section with Centered Logo - 2025-01-XX */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-6"
        >
          {/* Centered Logo with Trade subtitle - 2025-01-XX */}
          <div className="flex justify-center mb-6">
            <Logo size="lg" showSubtitle={true} subtitle="TRADE" animated={true} />
          </div>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div className="text-center sm:text-left">
              <p className="text-gray-400 text-sm sm:text-base">
                Browse items from online players • {filteredAndSortedItems.length} of {items.length} items
              </p>
            </div>
            <div className="flex items-center justify-center sm:justify-end gap-3">
              <button
                onClick={refresh}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded font-mono text-sm transition-colors"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>

          {/* Search Bar - 2025-01-XX */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-cyan-400/60" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search items, players, or item types..."
              className="w-full pl-10 pr-4 py-3 bg-black/50 border border-cyan-400/40 rounded text-cyan-300 placeholder-gray-500 focus:outline-none focus:border-cyan-400 font-mono transition-all duration-200"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-cyan-400"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Filters with Icon Buttons - 2025-01-XX */}
          <div className="flex flex-col gap-3 mb-6">
            {/* Icon Filter Buttons - 2025-01-XX */}
            <div className="flex flex-wrap items-center gap-2">
              <Filter className="w-4 h-4 text-cyan-400" />
              <span className="text-sm text-gray-400 font-mono">Filter by Type:</span>
              <button
                onClick={() => setSelectedClass('all')}
                className={`px-2 py-1.5 rounded border transition-all duration-200 ${
                  selectedClass === 'all'
                    ? 'bg-cyan-600 border-cyan-400 text-white'
                    : 'bg-black/50 border-cyan-400/40 text-cyan-300 hover:border-cyan-400'
                }`}
                title="All Items"
              >
                <Package className="w-4 h-4" />
              </button>
              {itemClasses.map(className => {
                const IconComponent = getItemIcon(className);
                return (
                  <button
                    key={className}
                    onClick={() => setSelectedClass(selectedClass === className ? 'all' : className)}
                    className={`px-2 py-1.5 rounded border transition-all duration-200 ${
                      selectedClass === className
                        ? 'bg-cyan-600 border-cyan-400 text-white'
                        : 'bg-black/50 border-cyan-400/40 text-cyan-300 hover:border-cyan-400'
                    }`}
                    title={className}
                  >
                    <IconComponent className="w-4 h-4" />
                  </button>
                );
              })}
            </div>
            
            {/* Dropdown Filters - 2025-01-XX */}
            <div className="flex flex-wrap gap-3">
              <select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                className="px-3 py-2 bg-black/50 border border-cyan-400/40 rounded text-cyan-300 text-sm font-mono focus:outline-none focus:border-cyan-400"
              >
                <option value="all">All Item Types</option>
                {itemClasses.map(className => (
                  <option key={className} value={className}>{className}</option>
                ))}
              </select>
              <select
                value={selectedPlayer}
                onChange={(e) => setSelectedPlayer(e.target.value)}
                className="px-3 py-2 bg-black/50 border border-cyan-400/40 rounded text-cyan-300 text-sm font-mono focus:outline-none focus:border-cyan-400"
              >
                <option value="all">All Players</option>
                {players.map(player => (
                  <option key={player} value={player}>{player}</option>
                ))}
              </select>
              {(selectedClass !== 'all' || selectedPlayer !== 'all') && (
                <button
                  onClick={() => {
                    setSelectedClass('all');
                    setSelectedPlayer('all');
                  }}
                  className="px-3 py-2 text-xs text-gray-400 hover:text-cyan-400 font-mono"
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>

          {/* Sort and View Options - 2025-01-XX */}
          <div className="flex flex-wrap items-center justify-between gap-4 mt-4 p-3 bg-slate-900/40 border border-cyan-500/20 rounded-lg">
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-400 font-mono flex items-center gap-2">
                <ArrowUpDown className="w-4 h-4" />
                Sort by:
              </span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                className="px-3 py-1.5 bg-black/50 border border-cyan-400/40 rounded text-cyan-300 text-sm font-mono focus:outline-none focus:border-cyan-400"
              >
                <option value="name">Name</option>
                <option value="level">Level</option>
                <option value="condition">Condition</option>
                <option value="uses">Uses</option>
                <option value="player">Player</option>
                <option value="class">Item Type</option>
              </select>
              <button
                onClick={() => setSortAscending(!sortAscending)}
                className="px-3 py-1.5 bg-black/50 border border-cyan-400/40 rounded text-cyan-300 text-sm font-mono hover:bg-black/70 transition-colors"
                title={sortAscending ? 'Ascending' : 'Descending'}
              >
                {sortAscending ? '↑' : '↓'}
              </button>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-400 font-mono">View:</span>
              <div className="flex gap-1 bg-black/50 border border-cyan-400/40 rounded p-1">
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded transition-colors ${
                    viewMode === 'list'
                      ? 'bg-cyan-600 text-white'
                      : 'text-gray-400 hover:text-cyan-400 hover:bg-cyan-400/10'
                  }`}
                  title="List View"
                >
                  <List className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded transition-colors ${
                    viewMode === 'grid'
                      ? 'bg-cyan-600 text-white'
                      : 'text-gray-400 hover:text-cyan-400 hover:bg-cyan-400/10'
                  }`}
                  title="Grid View"
                >
                  <Grid3x3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('detailed')}
                  className={`p-2 rounded transition-colors ${
                    viewMode === 'detailed'
                      ? 'bg-cyan-600 text-white'
                      : 'text-gray-400 hover:text-cyan-400 hover:bg-cyan-400/10'
                  }`}
                  title="Detailed View"
                >
                  <Square className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Loading State - 2025-01-XX */}
        {loading && items.length === 0 && (
          <div className="text-center py-20">
            <Loader2 className="w-12 h-12 text-cyan-400 animate-spin mx-auto mb-4" />
            <p className="text-cyan-300 font-mono">Loading trading data...</p>
          </div>
        )}

        {/* Error State - 2025-01-XX */}
        {error && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-6"
          >
            <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                <div>
                  <h3 className="text-red-400 font-mono font-bold mb-1">ERROR</h3>
                  <p className="text-red-300 text-sm">{error}</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Items Display with Infinite Scroll - 2025-01-XX */}
        <AnimatePresence mode="wait">
          {filteredAndSortedItems.length > 0 ? (
            <>
              <motion.div
                key={`items-${viewMode}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className={
                  viewMode === 'list'
                    ? 'space-y-1.5'
                    : viewMode === 'grid'
                    ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-2'
                    : 'grid grid-cols-1 lg:grid-cols-2 gap-3'
                }
              >
                {visibleItems.map((item, index) => (
                <motion.div
                  key={item._oid}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.02 }}
                  whileHover={viewMode !== 'list' ? { scale: 1.01, y: -2 } : {}}
                  className={`bg-slate-900/60 border border-cyan-500/30 rounded hover:border-cyan-400/50 transition-all duration-300 group ${
                    viewMode === 'list'
                      ? 'p-2 flex items-center justify-between gap-3'
                      : viewMode === 'detailed'
                      ? 'p-4'
                      : 'p-2.5'
                  }`}
                >
                  {viewMode === 'list' ? (
                    /* List View - Compact Horizontal Layout - 2025-01-XX */
                    <>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {(() => {
                            const IconComponent = getItemIcon(item._className);
                            return <IconComponent className="w-4 h-4 text-cyan-400/80 flex-shrink-0" />;
                          })()}
                          <h3 className="font-bold text-white text-base group-hover:text-cyan-300 transition-colors truncate flex-1">
                            {item.displayName}
                          </h3>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap text-xs">
                          <span className="font-mono text-cyan-400/80 bg-cyan-400/10 px-2 py-0.5 rounded">
                            {item._className}
                          </span>
                          <span className="font-mono text-gray-400">
                            @{item.playerName}
                          </span>
                          {item.stats?.level && (
                            <span className="font-mono text-gray-500">Lv.{item.stats.level}</span>
                          )}
                          {item.stats?.condition !== undefined && item.stats?.maxCondition !== undefined && (
                            <span className="font-mono text-gray-500">
                              {item.stats.condition}/{item.stats.maxCondition}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => handleWhisper(item)}
                          className="flex-shrink-0 flex items-center gap-2 px-3 py-1.5 bg-cyan-600 hover:bg-cyan-500 text-white rounded font-mono text-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={whisperStatus?.itemId === item._oid && whisperStatus.success}
                        >
                          {whisperStatus?.itemId === item._oid && whisperStatus.success ? (
                            <>
                              <MessageSquare className="w-3 h-3" />
                              Sent!
                            </>
                          ) : (
                            <>
                              <MessageSquare className="w-3 h-3" />
                              Whisper
                            </>
                          )}
                        </button>
                        {whisperStatus?.itemId === item._oid && whisperStatus.error && (
                          <span className="text-xs text-red-400 font-mono">{whisperStatus.error}</span>
                        )}
                      </div>
                    </>
                  ) : (
                    /* Grid/Detailed View - Full Card Layout - 2025-01-XX */
                    <>
                      {/* Item Header with Icon - 2025-01-XX */}
                      <div className={`${viewMode === 'detailed' ? 'mb-3' : 'mb-1.5'}`}>
                        <div className="flex items-start gap-2 mb-0.5">
                          {(() => {
                            const IconComponent = getItemIcon(item._className);
                            return (
                              <IconComponent className={`text-cyan-400/80 flex-shrink-0 ${
                                viewMode === 'detailed' ? 'w-5 h-5 mt-0.5' : 'w-4 h-4 mt-0.5'
                              }`} />
                            );
                          })()}
                          <h3 className={`font-bold text-white group-hover:text-cyan-300 transition-colors flex-1 ${
                            viewMode === 'detailed' ? 'text-base' : 'text-sm'
                          }`}>
                            {item.displayName}
                          </h3>
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-xs font-mono text-cyan-400/80 bg-cyan-400/10 px-1.5 py-0.5 rounded">
                            {item._className}
                          </span>
                          <span className="text-xs font-mono text-gray-400">
                            @{item.playerName}
                          </span>
                        </div>
                      </div>

                      {/* Item Details - 2025-01-XX */}
                      <div className={`space-y-1 ${viewMode === 'detailed' ? 'mb-3' : 'mb-2'}`}>
                    {/* Item Stats - 2025-01-XX */}
                    {(item.stats?.level || item.stats?.useCount !== undefined || (item.stats?.condition !== undefined && item.stats?.maxCondition !== undefined)) && (
                      <div className="text-xs space-y-0.5 pb-1.5 border-b border-cyan-500/20">
                        {item.stats?.level !== undefined && (
                          <div className="flex justify-between items-center">
                            <span className="text-gray-400">Level:</span>
                            <span className="text-cyan-300 font-mono">{item.stats.level}</span>
                          </div>
                        )}
                        {item.stats?.useCount !== undefined && (
                          <div className="flex justify-between items-center">
                            <span className="text-gray-400">Uses:</span>
                            <span className="text-cyan-300 font-mono">{item.stats.useCount}</span>
                          </div>
                        )}
                        {item.stats?.condition !== undefined && item.stats?.maxCondition !== undefined && (
                          <div className="flex justify-between items-center">
                            <span className="text-gray-400">Condition:</span>
                            <span className="text-cyan-300 font-mono">
                              {item.stats.condition} / {item.stats.maxCondition}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                    {item.Consumable?.buffName && (
                      <div className="text-xs text-gray-400">
                        <span className="text-cyan-400">Buff:</span> {item.Consumable.buffName}
                      </div>
                    )}
                    {item.Consumable?.modifiers && item.Consumable.modifiers.length > 0 && (
                      <div className="text-xs">
                        <p className="text-cyan-400 mb-0.5 font-mono">Modifiers:</p>
                        <div className="space-y-0.5 pl-1.5 border-l-2 border-cyan-500/20">
                          {item.Consumable.modifiers.map((mod: any, idx: number) => (
                            <div key={idx} className="text-gray-300 font-mono">
                              {Object.entries(mod).map(([key, value]) => (
                                <div key={key} className="flex justify-between">
                                  <span className="text-gray-400">{key}:</span>
                                  <span className="text-cyan-300">+{String(value)}</span>
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                      </div>

                      {/* Whisper Button - 2025-01-XX */}
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => handleWhisper(item)}
                          className={`w-full flex items-center justify-center gap-1.5 px-2 py-1.5 bg-cyan-600 hover:bg-cyan-500 text-white rounded font-mono transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                            viewMode === 'detailed' ? 'text-sm py-2' : 'text-xs'
                          }`}
                          disabled={whisperStatus?.itemId === item._oid && whisperStatus.success}
                        >
                          {whisperStatus?.itemId === item._oid && whisperStatus.success ? (
                            <>
                              <MessageSquare className="w-3 h-3" />
                              Sent!
                            </>
                          ) : (
                            <>
                              <MessageSquare className="w-3 h-3" />
                              Whisper
                            </>
                          )}
                        </button>
                        {whisperStatus?.itemId === item._oid && whisperStatus.error && (
                          <span className={`text-xs text-red-400 font-mono text-center ${
                            viewMode === 'detailed' ? 'text-sm' : ''
                          }`}>{whisperStatus.error}</span>
                        )}
                      </div>
                    </>
                  )}
                </motion.div>
              ))}
              </motion.div>
              
              {/* Infinite scroll trigger - 2025-01-XX */}
              {visibleItemsCount < filteredAndSortedItems.length && (
                <div ref={observerTarget} className="flex justify-center items-center py-8">
                  <div className="flex items-center gap-3 text-cyan-400/60">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="font-mono text-sm">
                      Loading more items... ({visibleItemsCount} of {filteredAndSortedItems.length})
                    </span>
                  </div>
                </div>
              )}
              
              {/* Show count when all items are loaded - 2025-01-XX */}
              {visibleItemsCount >= filteredAndSortedItems.length && filteredAndSortedItems.length > 30 && (
                <div className="flex justify-center items-center py-4">
                  <span className="font-mono text-sm text-gray-400">
                    Showing all {filteredAndSortedItems.length} items
                  </span>
                </div>
              )}
            </>
          ) : !loading && (
            <motion.div
              key="no-items"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center py-20"
            >
              <Search className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 font-mono text-lg mb-2">No items found</p>
              <p className="text-gray-500 text-sm">
                {searchQuery || selectedClass !== 'all' || selectedPlayer !== 'all'
                  ? 'Try adjusting your search or filters'
                  : 'No items available from online players'}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Scroll to Top Button - Positioned above heart button, matching Header button style - 2025-01-XX */}
      <AnimatePresence>
        {showScrollToTop && (
          <motion.div
            className="fixed z-50 transition-all duration-500 ease-in-out"
            style={{
              bottom: `${buttonPosition.bottom + 120}px`, // 60px above heart button (which is 60px above music button)
              right: `${buttonPosition.right}px`
            }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.3, delay: 0.3 }}
          >
            <button
              onClick={scrollToTop}
              className="p-3 bg-slate-800/90 border border-cyan-500/50 hover:border-cyan-400 hover:shadow-cyan-400/30 hover:shadow-lg rounded-full text-gray-300 hover:text-cyan-400 transition-all duration-300 hover:bg-slate-700/90"
              aria-label="Scroll to top"
            >
              <ArrowUp className="w-5 h-5" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

